import { useState } from 'react';
import { fetchApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

export default function CountersignNDA({ ndaRecord, companyName, inventorName, onComplete }) {
  const { profile } = useAuth();
  const [signature, setSignature] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isCountersign = ndaRecord.countersign_required;
  const actionLabel = isCountersign ? 'Countersign & Execute' : 'Confirm & Acknowledge';
  const endpoint = isCountersign ? '/api/nda/countersign' : '/api/nda/acknowledge';

  const handleSubmit = async () => {
    if (!signature.trim()) return;
    setSubmitting(true);
    try {
      await fetchApi(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          nda_record_id: ndaRecord.id,
          signature_text: signature.trim(),
        }),
      });
      onComplete && onComplete();
    } catch (err) {
      alert('Failed: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="glass p-7 max-w-2xl">
      <h2 className="font-heading text-xl text-vault-navy mb-5">
        {isCountersign ? 'Countersign NDA' : 'Acknowledge NDA Receipt'}
      </h2>

      {/* Inventor's signature info */}
      <div className="bg-gray-50 rounded-btn p-5 mb-6">
        <div className="text-[12px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Inventor Signature on File</div>
        <div className="space-y-2 text-sm">
          <div><span className="text-gray-400">Signed by:</span> <span className="text-vault-navy font-medium">{ndaRecord.inventor_signature_text}</span></div>
          <div><span className="text-gray-400">Date:</span> <span className="text-vault-navy">{new Date(ndaRecord.inventor_signed_at).toLocaleString()}</span></div>
        </div>
      </div>

      {/* NDA Document link */}
      <div className="mb-6">
        <a href={ndaRecord.document_url} target="_blank" rel="noreferrer" className="text-vault-gold font-semibold text-sm hover:underline">
          View NDA Document →
        </a>
      </div>

      {/* E-SIGN Disclosure */}
      <div className="bg-amber-50 border border-amber-200 rounded-btn p-4 mb-6">
        <p className="text-sm text-amber-800 leading-relaxed">
          {isCountersign
            ? `By typing your full legal name below, you are countersigning this NDA on behalf of ${companyName}, confirming your agreement with ${inventorName}.`
            : `By clicking Confirm, ${profile?.display_name} acknowledges on behalf of ${companyName} that you have received the signed NDA from ${inventorName} dated ${new Date(ndaRecord.inventor_signed_at).toLocaleString()} and confirm it is binding.`
          }
          {' '}This constitutes a legally binding electronic signature under the Electronic Signatures in Global and National Commerce Act (E-SIGN Act).
        </p>
      </div>

      {/* Signature field */}
      <div className="mb-6">
        <label className="block text-[12px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
          Type Your Full Legal Name
        </label>
        <input
          className="w-full px-4 py-3 rounded-input border-2 border-vault-border text-sm text-vault-navy bg-white/70 focus:border-vault-gold focus:outline-none transition-colors"
          style={{ fontFamily: "'EB Garamond', Georgia, serif", fontSize: 18 }}
          value={signature}
          onChange={e => setSignature(e.target.value)}
          placeholder="Your full legal name"
        />
      </div>

      <button
        onClick={handleSubmit}
        disabled={!signature.trim() || submitting}
        className="w-full px-6 py-3 rounded-btn text-sm font-semibold text-white shadow-btn disabled:opacity-50 transition-all"
        style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
      >
        {submitting ? 'Processing...' : actionLabel}
      </button>
    </div>
  );
}
